import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import axios from 'axios';
import _ from 'lodash';

import { useGetUserDetails } from '../../hooks/useGetUserDetails';
import { useModal } from '../../providers/ModalProvider';
import PrimaryButton from '../Buttons/PrimaryButton/PrimaryButton';
import CityAutocomplete from '../CityAutocomplete/CityAutocomplete';
import CountrySelect from '../CountrySelect/CountrySelect';

const User_Edit = () => {
  const dataUser = useSelector((state) => state?.auth?.userDataDetail);

  const params = useParams();
  const { t } = useTranslation();
  const { isLoading, productsInCartCounter, addresses } = useGetUserDetails();

  const [initialState, setInitialState] = useState({
    business_name: dataUser?.business_name,
    VATNUM: dataUser?.VATNUM,
    email: dataUser?.email,
    PHONE: dataUser?.PHONE,
    mainAddress: {},
    secondaryAddresses: [],
    newAddresses: [],

    address_check: false,
  });

  useEffect(() => {
    const formattedAddresses = addresses.map((a) => ({
      id: a.id,
      ISPRIMARY: a.ISPRIMARY,
      STREET: a?.STREET,
      ADDRESSNAME: a?.ADDRESSNAME,
      CITY: a.CITY,
      COUNTRYREGIONID: a.COUNTRYREGIONID,
      ZIPCODE: a.ZIPCODE,
      COUNTY: a.COUNTY,
      STATE: a.STATE || '', // Aggiungi campo STATE
    }));

    setInitialState((prevState) => {
      return {
        ...prevState,
        mainAddress: formattedAddresses.find((a) => a.ISPRIMARY === 'Sì') || {},
        secondaryAddresses: formattedAddresses.filter((a) => a.ISPRIMARY !== 'Sì'),
        newAddresses: [],
      };
    });
  }, [addresses]);

  const [userData, setUserData] = useState(initialState);

  useEffect(() => {
    setUserData(initialState);
  }, [initialState]);

  const handleChange = (e) => {
    const data = { ...userData };
    data[e.target.name] = e.target.value;
    setUserData(data);
  };

  const handleMainAddressChange = (e) => {
    setUserData((prevState) => {
      return {
        ...prevState,
        mainAddress: {
          ...prevState.mainAddress,
          [e.target.name]: e.target.value,
        },
      };
    });
  };

  const handleSecondaryAddressChange = (id, e) => {
    setUserData((prevState) => {
      const secondaryAddresses = prevState.secondaryAddresses.map((a) =>
        a.id !== id ? a : { ...a, [e.target.name]: e.target.value }
      );

      return {
        ...prevState,
        secondaryAddresses,
      };
    });
  };

  const handleNewAddressChange = (index, e) => {
    const name = e.target.name;
    const value = e.target.value;

    const newAddresses = userData.newAddresses.map((a, i) =>
      index !== i ? a : { ...a, [name]: value }
    );

    setUserData((prevState) => ({
      ...prevState,
      newAddresses,
    }));
  };

  // Handler per l'autocompletamento città nuovi indirizzi (solo per nuovi indirizzi italiani)
  const handleNewAddressCitySelect = (index, cityDetails) => {
    setUserData((prevState) => {
      const newAddresses = prevState.newAddresses.map((a, i) =>
        index !== i ? a : { 
          ...a, 
          CITY: cityDetails.city,
          COUNTY: cityDetails.province,
          ZIPCODE: cityDetails.postal_code,
          STATE: cityDetails.region // Compila anche STATE con la regione
        }
      );

      return {
        ...prevState,
        newAddresses,
      };
    });
  };

  // Funzione per verificare se un paese è l'Italia
  const isItalySelected = (countryCode) => {
    return countryCode === 'ITA' || countryCode === 'IT';
  };

  const modal = useModal();

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Verifica la corrispondenza città-CAP per i nuovi indirizzi italiani prima di inviare
    for (let i = 0; i < userData.newAddresses.length; i++) {
      const address = userData.newAddresses[i];
      const country = address.COUNTRYREGIONID || '';
      const city = address.CITY || '';
      const zip = address.ZIPCODE || '';

      if ((country === 'ITA' || country === 'IT') && city.trim() !== '' && zip.trim() !== '') {
        try {
          const response = await axios.get('/v1/check-cities-cap-state-ax', {
            params: { search: city }
          });
          const results = Array.isArray(response.data) ? response.data : [];

          const normalizedZip = String(zip).trim().replace(/\s+/g, '');
          const match = results.some((r) => {
            if (!r.postal_code) return false;
            const rp = String(r.postal_code).trim().replace(/\s+/g, '');
            return rp === normalizedZip;
          });

          if (!match) {
            modal.showAttention({
              subtitle: <span dangerouslySetInnerHTML={{ __html: t('city_zip_mismatch') }} />
            });
            return; // Blocca l'invio del form
          }
        } catch (err) {
          console.error('Error verifying city-zip:', err);
          // In caso di errore di rete, permettiamo comunque l'invio
        }
      }
    }

    modal.showLoading({
      allowOutsideClick: false,
    });

    await axios
      .post('/v1/profile-manager-request-update', userData, {
        headers: {
          Authorization: `Bearer ${dataUser?.token}`,
        },
      })
      .then(function (response) {
        modal.showSuccess({
          subtitle: t('settings_form_success_note'),
          onConfirm: () => {
            window.location.href = '/dashboard-list';
          },
        });
      })
      .catch(function (err) {
        modal.showAttention({ subtitle: err?.response?.data?.message || t('oops_something_wrong') });
      });
  };

  const handleAddSecondaryAddressChange = (e) => {
    const newAddresses = e.target.checked
      ? [
          {
            id: null,
            ISPRIMARY: '',
            STREET: '',
            ADDRESSNAME: '',
            CITY: '',
            COUNTRYREGIONID: 'ITA',
            ZIPCODE: '',
            COUNTY: '',
            STATE: '', 
          },
        ]
      : [];

    setUserData({ ...userData, newAddresses, address_check: e.target.checked });
  };

  useEffect(() => {
    document.getElementById('home_page_class').classList.remove('home_page');
  }, [params?.id]);

  const disabledSubmit = useMemo(() => {
    return _.isEqual(initialState, userData);
  }, [initialState, userData]);

  //console.log('userData', userData);
  //console.log('dataUser', dataUser);
  return (
    <>
      <div className="main_content change_request">
        <div className="change_request_section">
          <div className="change_request_wrapper container">
            <div className="change_request_title">
              <h2>{t('richiesta_mod_info')}</h2>
              <Link to={'/dashboard-list'}>{t('Chiudi')}</Link>
              <p>{t('form_richiesta_mod_info')}</p>
            </div>
            {isLoading ? (
              <div className="loader"></div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="info_block utente_info">
                  <div className="block_title">
                    <h3>{t('Informazioni utente')}</h3>
                  </div>
                  <div className="form-input">
                    <label>{t('reglone_societe_required')}</label>
                    <input
                      type="text"
                      required
                      name="business_name"
                      onChange={(e) => handleChange(e)}
                      value={userData?.business_name}
                      placeholder={t('reglone_societe_required')}
                    />
                  </div>
                  <div className="form-input">
                    <label>{t('partial_vat_required')}</label>
                    <input
                      type="text"
                      required
                      name="VATNUM"
                      onChange={(e) => handleChange(e)}
                      value={userData?.VATNUM ?? dataUser?.vat_number}
                      placeholder={t('partial_vat_required')}
                    />
                  </div>
                  <div className="form-input">
                    <label>{t('indirizzo_email_required')}</label>
                    <input
                      type="email"
                      required
                      name="email"
                      onChange={(e) => handleChange(e)}
                      value={userData?.email}
                      placeholder={t('indirizzo_email_required')}
                    />
                  </div>
                  <div className="form-input">
                    <label>{t('telefono_required')}</label>
                    <input
                      type="text"
                      required
                      name="PHONE"
                      onChange={(e) => handleChange(e)}
                      value={userData?.PHONE ?? dataUser?.mobile_no}
                      placeholder={t('telefono_required')}
                    />
                  </div>
                </div>
                {isLoading ? <div className="loader"></div> : null}
                {userData.mainAddress ? (
                  <div className="info_block principale_info">
                    <div className="block_title">
                      <h3>{t('Indirizzo principale')}</h3>
                    </div>
                    <div className="form-input">
                      <label>{t('indirizzo_required')}</label>
                      <input
                        type="text"
                        required={userData.newAddresses.length === 0}
                        name="STREET"
                        onChange={(e) => handleMainAddressChange(e)}
                        value={userData.mainAddress?.STREET && userData.mainAddress.STREET.trim() !== '' ? userData.mainAddress.STREET : dataUser?.village}
                        placeholder={t('indirizzo_required')}
                      />
                    </div>
                    <div className="form-input">
                      <label>{t('nominativo_required')}</label>
                      <input
                        type="text"
                        required={userData.newAddresses.length === 0}
                        name="ADDRESSNAME"
                        onChange={(e) => handleMainAddressChange(e)}
                        value={userData.mainAddress?.ADDRESSNAME && userData.mainAddress.ADDRESSNAME.trim() !== '' ? userData.mainAddress.ADDRESSNAME : dataUser?.business_name}
                        placeholder={t('nominativo_required')}
                      />
                    </div>
                    <div className="form-input">
                      <label>{t('city_required')}</label>
                      <input
                        type="text"
                        required={userData.newAddresses.length === 0}
                        name="CITY"
                        onChange={(e) => handleMainAddressChange(e)}
                        value={userData.mainAddress?.CITY && userData.mainAddress.CITY.trim() !== '' ? userData.mainAddress.CITY : dataUser?.city}
                        placeholder={t('city_required')}
                      />
                    </div>
                    <div className="form-input">
                      <label>{t('province_required')}</label>
                      <input
                        type="text"
                        required={userData.newAddresses.length === 0}
                        name="COUNTY"
                        onChange={(e) => handleMainAddressChange(e)}
                        value={userData.mainAddress?.COUNTY && userData.mainAddress.COUNTY.trim() !== '' ? userData.mainAddress.COUNTY : dataUser?.COUNTY}
                        placeholder={t('province_required')}
                      />
                    </div>
                    <div className="form-input">
                      <label>{t('cap_required')}</label>
                      <input
                        type="text"
                        required={userData.newAddresses.length === 0}
                        name="ZIPCODE"
                        onChange={(e) => handleMainAddressChange(e)}
                        value={userData.mainAddress?.ZIPCODE && userData.mainAddress.ZIPCODE.trim() !== '' ? userData.mainAddress.ZIPCODE : dataUser?.postal_code}
                        placeholder={t('cap_required')}
                      />
                    </div>
                    <div className="form-input">
                      <label>{t('paese_required')}</label>
                      <CountrySelect
                        value={userData.mainAddress?.COUNTRYREGIONID && userData.mainAddress.COUNTRYREGIONID.trim() !== '' ? userData.mainAddress.COUNTRYREGIONID : dataUser?.country_code}
                        onChange={(e) => handleMainAddressChange(e)}
                        placeholder={t('paese_required')}
                        required={userData.newAddresses.length === 0}
                        name="COUNTRYREGIONID"
                      />
                    </div>
                    {/* STATE rimosso per indirizzo principale (presente solo per i nuovi indirizzi) */}
                  </div>
                ) : null}
                {userData.secondaryAddresses.length > 0 ? (
                  <>
                    <div className="info_block secondari_info">
                      <div className="block_title">
                        <h3>{t('Indirizzi secondari')}</h3>
                      </div>
                    </div>
                    {userData.secondaryAddresses.map((address, index) => (
                      <div
                        className={`info_block last_info ${
                          index !== userData.secondaryAddresses.length - 1 ? 'border-bottom' : ''
                        }`}
                      >
                        <div className="form-input">
                          <label>{t('indirizzo_required')}</label>
                          <input
                            type="text"
                            required={userData.newAddresses.length === 0}
                            name="STREET"
                            onChange={(e) => handleSecondaryAddressChange(address.id, e)}
                            value={address?.STREET}
                            placeholder={t('indirizzo_required')}
                          />
                        </div>
                        <div className="form-input">
                          <label>{t('nominativo_required')}</label>
                          <input
                            type="text"
                            required={userData.newAddresses.length === 0}
                            name="ADDRESSNAME"
                            onChange={(e) => handleSecondaryAddressChange(address.id, e)}
                            value={address?.ADDRESSNAME}
                            placeholder={t('nominativo_required')}
                          />
                        </div>
                        <div className="form-input">
                          <label>{t('city_required')}</label>
                          <input
                            type="text"
                            required={userData.newAddresses.length === 0}
                            name="CITY"
                            onChange={(e) => handleSecondaryAddressChange(address.id, e)}
                            value={address?.CITY}
                            placeholder={t('city_required')}
                          />
                        </div>
                        <div className="form-input">
                          <label>{t('province_required')}</label>
                          <input
                            type="text"
                            required={userData.newAddresses.length === 0}
                            name="COUNTY"
                            onChange={(e) => handleSecondaryAddressChange(address.id, e)}
                            value={address?.COUNTY ?? dataUser?.COUNTY}
                            placeholder={t('province_required')}
                          />
                        </div>
                        <div className="form-input">
                          <label>{t('cap_required')}</label>
                          <input
                            type="text"
                            required={userData.newAddresses.length === 0}
                            name="ZIPCODE"
                            onChange={(e) => handleSecondaryAddressChange(address.id, e)}
                            value={address?.ZIPCODE}
                            placeholder={t('cap_required')}
                          />
                        </div>
                        <div className="form-input">
                          <label>{t('paese_required')}</label>
                          <CountrySelect
                            value={address?.COUNTRYREGIONID}
                            onChange={(e) => handleSecondaryAddressChange(address.id, e)}
                            placeholder={t('paese_required')}
                            required={userData.newAddresses.length === 0}
                            name="COUNTRYREGIONID"
                          />
                        </div>
                        {/* STATE rimosso per indirizzi secondari esistenti (presente solo per i nuovi indirizzi) */}
                      </div>
                    ))}
                  </>
                ) : null}
                {!dataUser.is_prospect ? (
                  <>
                  <div className="info_block sub_address_info">
                  <div className="sub_check">
                    <input
                      checked={userData.address_check}
                      onChange={handleAddSecondaryAddressChange}
                      type="checkbox"
                      id="address_check"
                      name="address_check"
                    />
                    <label htmlFor="address_check">{t('indirizzo_secondario_aggiuntivo')}</label>
                  </div>
                </div>
                {userData.newAddresses.length > 0 ? (
                  <>
                    <div className="info_block new_address">
                      <div className="block_title">
                        <h3>{t('Indirizzi secondari')}</h3>
                      </div>
                    </div>
                    {userData.newAddresses.map((address, index) => (
                      <div
                        className={`info_block last_info ${
                          index !== userData.newAddresses.length - 1 ? 'border-bottom' : ''
                        }`}
                      >
                        <div className="form-input">
                          <label>{t('indirizzo_required')}</label>
                          <input
                            type="text"
                            required
                            name="STREET"
                            onChange={(e) => handleNewAddressChange(index, e)}
                            value={address?.STREET}
                            placeholder={t('indirizzo_required')}
                          />
                        </div>
                        <div className="form-input">
                          <label>{t('nominativo_required')}</label>
                          <input
                            type="text"
                            required
                            name="ADDRESSNAME"
                            onChange={(e) => handleNewAddressChange(index, e)}
                            value={address?.ADDRESSNAME}
                            placeholder={t('nominativo_required')}
                          />
                        </div>
                        <div className="form-input">
                          <label>{t('city_required')}</label>
                          {isItalySelected(address?.COUNTRYREGIONID) ? (
                            <CityAutocomplete
                              value={address?.CITY}
                              onChange={(e) => handleNewAddressChange(index, e)}
                              onCitySelect={(cityDetails) => handleNewAddressCitySelect(index, cityDetails)}
                              placeholder={t('city_required')}
                              required={true}
                              name="CITY"
                            />
                          ) : (
                            <input
                              type="text"
                              required
                              name="CITY"
                              onChange={(e) => handleNewAddressChange(index, e)}
                              value={address?.CITY}
                              placeholder={t('city_required')}
                            />
                          )}
                        </div>
                        <div className="form-input">
                          <label>{t('province_required')}</label>
                          <input
                            type="text"
                            required
                            name="COUNTY"
                            onChange={(e) => handleNewAddressChange(index, e)}
                            value={address?.COUNTY}
                            placeholder={t('province_required')}
                          />
                        </div>
                        <div className="form-input">
                          <label>{t('cap_required')}</label>
                          <input
                            type="text"
                            required
                            name="ZIPCODE"
                            onChange={(e) => handleNewAddressChange(index, e)}
                            value={address?.ZIPCODE}
                            placeholder={t('cap_required')}
                          />
                        </div>
                        <div className="form-input">
                          <label>{t('paese_required')}</label>
                          <CountrySelect
                            value={address?.COUNTRYREGIONID}
                            onChange={(e) => handleNewAddressChange(index, e)}
                            placeholder={t('paese_required')}
                            required={true}
                            name="COUNTRYREGIONID"
                          />
                        </div>
                        {/* Campo nascosto STATE - compilato automaticamente per indirizzi italiani */}
                        <input
                          type="hidden"
                          name="STATE"
                          value={address?.STATE || ''}
                        />
                      </div>
                    ))}
                  </>
                ) : null}
                <div className="action">
                  <PrimaryButton fullWidth disabled={disabledSubmit}>
                    {t('invia_richiesta')}
                  </PrimaryButton>
                </div>
                  </>
                ) :null}
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default User_Edit;